import { Box, Collapse } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { isTelematics, isUsedForBusiness } from '@acorn-portal/shared/src/utils';
import {
	ANNUAL_MILEAGE,
	ANNUAL_MILEAGE_YD,
	BUSINESS_MILEAGE,
	COVER_LEVEL,
	TELEMATICS_ANNUAL_MILEAGE_HELP_TEXT,
	STREET_COVER_VEHICLE_USED_FOR,
} from '@acorn-portal/shared/src/utils/constants/modalText';
import getClosestMileage from '@acorn-portal/shared/src/utils/getClosestMileage';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import AnnualMileageCalculator from '@acorn-portal/shared/src/components/calculators/AnnualMileageCalculator';
import { updateChangeOfUsage } from '@acorn-portal/shared/src/store/renewalChanges.slice';
import useRateRenewal from '@acorn-portal/shared/src/hooks/useRateRenewal';
import useRenewalNavigation from '@acorn-portal/shared/src/hooks/useRenewalNavigation';
import ChangeOfUsageOrMileageSchema from '@acorn-portal/shared/src/utils/validation/ChangeOfUsageOrMileageSchema';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IChangeUsageOrMileageForm {
	vehicleUse: string;
	levelOfCover: string;
	annualMileage: string;
	businessMileage?: string;
}

export interface Option {
	[key: string]: unknown;
}

const ChangeUsageOrMileage: FC = () => {
	const [open, toggleModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const { goToRenewalHome } = useRenewalNavigation();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const lists = useAppSelector((redux) => redux.referenceData);
	const renewalState = useAppSelector((redux) => redux.renewal);
	const { rateRenewal, loadingRenewalQuote } = useRateRenewal();
	const isTaxi = fullPolicy.product === Product.TAXI;
	const isMyPolicy = fullPolicy.product === Product.TELEMATICS && import.meta.env.VITE_AUTH_REALM === 'MyPolicy';

	const GetBusinessMillageOptions = (annualMileage: string) => {
		return (
			lists.millageIncrementOptions.filter((option) => (option.value as number) <= parseInt(annualMileage, 10)) || []
		);
	};

	// Remove all options which are not StreetCover related.
	const filteredVehicleUseOptions = useMemo(() => {
		return lists.vehicleUseOptions
			.filter((x) =>
				[
					'4', // social, domestic & pleasure
					'23', // Carriage of own goods for hire & reward
				].includes(x.value as string),
			)
			.map((x) => {
				return { ...x, isButton: true } as Option;
			}) as IOptions[];
	}, [lists.vehicleUseOptions]);

	useEffect(() => {
		setLoading(loadingRenewalQuote);
		if (!renewalState.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
	}, [loadingRenewalQuote]);

	const initialValues = {
		vehicleUse: renewalState.vehicleDetails.vehicleUse,
		levelOfCover: renewalState.vehicleDetails.coverDefinitionCode ?? '',
		annualMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			renewalState.vehicleDetails.annualMileage,
		),
		businessMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			renewalState.vehicleDetails.businessMileage,
		),
	};

	const onSubmit = async (
		values: IChangeUsageOrMileageForm,
		formikHelpers: FormikHelpers<IChangeUsageOrMileageForm>,
	) => {
		if (
			isUsedForBusiness(values.vehicleUse) &&
			(values.businessMileage === undefined || values.businessMileage === '0')
		) {
			formikHelpers.setFieldError('businessMileage', 'Please provide business mileage');
			formikHelpers.setFieldTouched('businessMileage', true, false);
			return;
		}
		setLoading(true);

		const renewalUpdated = {
			...renewalState,
			vehicleDetails: {
				...renewalState.vehicleDetails,
				annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
				businessMileage: values.businessMileage ? parseInt(values.businessMileage.toString(), 10) : 0,
				coverDefinitionCode: values.levelOfCover,
				vehicleUse: values.vehicleUse,
			},
		};

		try {
			setLoading(true);
			dispatch(updateChangeOfUsage(true));
			await rateRenewal({
				renewalState: renewalUpdated,
				fullPolicy,
			});
		} catch (error) {
			console.error(error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	const RenewalCoverStartDate = fullPolicy.renewalDetails?.renewalCoverDetails.startDate;

	return (
		<JourneyCardLayout pageTitle='Change of Usage & Mileage' loading={loading}>
			<Formik<IChangeUsageOrMileageForm>
				key={JSON.stringify(renewalState.newRenewalTransactionId)}
				validationSchema={ChangeOfUsageOrMileageSchema(fullPolicy)}
				initialValues={initialValues}
				onSubmit={onSubmit}
				onReset={goToRenewalHome}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						<ToggleButtonFormField
							label='What will you be using the vehicle for?'
							name='vehicleUse'
							id='vehicleUse'
							value={formik.values.vehicleUse}
							onChange={(value) => {
								formik.setFieldValue('vehicleUse', value);
								if (!isUsedForBusiness(value)) {
									formik.setFieldValue('businessMileage', undefined);
								}
							}}
							options={filteredVehicleUseOptions}
							error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
							helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
							modal={{
								title: STREET_COVER_VEHICLE_USED_FOR.header,
								description: STREET_COVER_VEHICLE_USED_FOR.text,
							}}
							product={fullPolicy.product}
						/>
						{!isTaxi && (
							<>
								<SelectFormField
									label='Annual mileage'
									name='annualMileage'
									id='annualMileage'
									value={formik.values.annualMileage}
									onChange={(value) => {
										formik.setFieldValue('annualMileage', value);
									}}
									options={lists.millageIncrementOptions as IOptions[]}
									modal={{
										title: ANNUAL_MILEAGE.header,
										description: !isTelematics(fullPolicy.product) ? ANNUAL_MILEAGE.text : ANNUAL_MILEAGE_YD.text,
									}}
									error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
									helperText={formik.touched.annualMileage && formik.errors.annualMileage}
								/>
								{isTelematics(fullPolicy.product) && (
									<Box
										sx={{
											position: 'relative',
											top: '-22px',
										}}
									>
										<AnnualMileageCalculator
											miles={parseInt(formik.values.annualMileage, 10)}
											id='annualMileageCalculator'
											helperText={TELEMATICS_ANNUAL_MILEAGE_HELP_TEXT}
										/>
									</Box>
								)}
							</>
						)}
						<Collapse in={isUsedForBusiness(formik.values.vehicleUse)}>
							<Box>
								<SelectFormField
									label='Business mileage'
									name='businessMileage'
									id='businessMileage'
									value={formik.values.businessMileage}
									onChange={(value) => {
										formik.setFieldValue('businessMileage', value);
									}}
									options={GetBusinessMillageOptions(formik.values.annualMileage) as IOptions[]}
									modal={{
										title: BUSINESS_MILEAGE.header,
										description: BUSINESS_MILEAGE.text,
									}}
									error={formik.touched.businessMileage && Boolean(formik.errors.businessMileage)}
									helperText={formik.touched.businessMileage && formik.errors.businessMileage}
								/>
							</Box>
						</Collapse>
						{!isMyPolicy && (
							<SelectFormField
								label='What level of cover do you require?'
								name='levelOfCover'
								id='levelOfCover'
								value={formik.values.levelOfCover}
								onChange={(value) => formik.setFieldValue('levelOfCover', value)}
								options={lists.levelOfCoverTypes.filter((x) => x.value !== 'TPO') as IOptions[]}
								error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
								helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
								modal={
									RenewalCoverStartDate
										? {
												title: COVER_LEVEL(RenewalCoverStartDate, fullPolicy.product).header,
												description: COVER_LEVEL(RenewalCoverStartDate, fullPolicy.product).text,
											}
										: undefined
								}
							/>
						)}

						<ActionButtons>
							<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
						<HelperModal
							open={open}
							onClose={() => {
								toggleModal(false);
							}}
							title='Discard The Changes?'
							description='Are you sure you wish to discard the changes?'
							closeText='No'
							submitText='Yes'
							onSubmit={goToRenewalHome}
						/>
					</Form>
				)}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeUsageOrMileage;
